import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import { Grid, Box } from "@material-ui/core";
import translate from "i18n/translate";
import translateIntl from "i18n/translateIntl";
import useStyles from "./operatorsStyles";
import {
  SrAlert,
  SrButton,
  SrInput,
  Typography,
  SrInputPassword,
} from "../../../common";
import { validations } from "utils";

const OperatorsForm = ({
  onReturn,
  initialValues,
  onSubmit,
  onDelete,
  edit,
  error,
}) => {
  const classes = useStyles();
  const intl = useIntl();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(translate("common.requiredField")),
    email: validations.email.required(translate("common.requiredField")),
    password: edit
      ? validations.password
      : validations.password.required(translate("common.requiredField")),
    confirmPassword: Yup.string().when("password", {
      is: (value) => !!value,
      then: validations.password
        .oneOf([Yup.ref("password"), null], translate("common.passwordMatch"))
        .required(translate("common.requiredField")),
    }),
  });

  return (
    <>
      <SrButton
        startIcon={<ArrowBackIcon color="primary" />}
        onClick={() => onReturn()}
      >
        {translate("common.return")}
      </SrButton>
      <Box
        maxWidth={720}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        margin="auto"
      >
        <Box paddingY={4}>
          <Typography variant="h3">
            {edit
              ? intl.formatMessage({ id: "operators.editUser" })
              : intl.formatMessage({ id: "operators.newUser" })}
          </Typography>
        </Box>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            isSubmitting,
            touched,
            values,
          }) => (
            <Form>
              <SrInput
                className={classes.operatorInputs}
                type="text"
                name="name"
                label={translate("common.name")}
                placeholder={translateIntl("common.name")}
                variant="outlined"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(errors.name && touched.name)}
                helperText={touched.name && errors.name}
                fullWidth
              />
              <SrInput
                className={classes.operatorInputs}
                type="text"
                name="email"
                label={translate("common.email")}
                placeholder={translateIntl("common.email")}
                variant="outlined"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(errors.email && touched.email)}
                helperText={touched.email && errors.email}
                fullWidth
                disabled={edit}
              />
              <SrInputPassword
                className={classes.operatorInputs}
                name="password"
                label={translate("common.password")}
                placeholder={translateIntl("common.password")}
                variant="outlined"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(errors.password && touched.password)}
                helperText={touched.password && errors.password}
                fullWidth
              />
              <SrInputPassword
                className={classes.operatorInputs}
                name="confirmPassword"
                label={translate("common.confirmPassword")}
                placeholder={translateIntl("common.confirmPassword")}
                variant="outlined"
                value={values.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  errors.confirmPassword && touched.confirmPassword
                )}
                helperText={touched.confirmPassword && errors.confirmPassword}
                fullWidth
              />
              {error && (
                <Box marginTop={2}>
                  <SrAlert severity="error">{error}</SrAlert>
                </Box>
              )}
              <Grid
                className={classes.addButton}
                container
                justifyContent="flex-start"
                alignItems="center"
                spacing={2}
              >
                <Grid item>
                  <SrButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    {edit
                      ? translate("operators.saveUser")
                      : translate("operators.addUser")}
                  </SrButton>
                </Grid>
                {edit && (
                  <Grid item>
                    <SrButton
                      color="secondary"
                      variant="contained"
                      onClick={onDelete}
                    >
                      {translate("operators.deleteUser")}
                    </SrButton>
                  </Grid>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};

OperatorsForm.defaultProps = {
  edit: false,
  onDelete: () => {},
};

OperatorsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onReturn: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  edit: PropTypes.bool,
  onDelete: PropTypes.func,
};

export default OperatorsForm;
